/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Collapse,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Transfer,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import MainLayout from '../../components/common/MainLayout';
import {
  useGetEventTemplatesQuery,
  useCreateEventTemplateMutation,
  useUpdateEventTemplateMutation,
  useDeleteEventTemplateMutation,
} from '../../api/eventTemplatesApi';
import { useGetPropertiesQuery } from '../../api/plantingEventsApi';
import type {
  EventTemplate,
  EventTemplateCategory,
  EventTemplateProperty,
  CreateEventTemplatePayload,
} from '../../types/eventTemplates';
import type { Property } from '../../types/plantingEvents';
import { useSelector } from 'react-redux';

const { Title, Paragraph, Text } = Typography;

const styles = {
  titleNoMargin: css({ margin: 0 }),
  paragraphNoMargin: css({ margin: 0 }),
  selectWidth220: css({ width: 220 }),
  searchMaxWidth: css({ maxWidth: 320 }),
  templateIcon: css({ fontSize: 20, marginRight: 8 }),
  colorDot: css({ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle' }),
  templateHeader: css({ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }),
  propertyTable: css({ marginTop: 8 }),
  headerRow: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }),
  templateActions: css({ display: 'flex', gap: 4 }),
};

const CATEGORY_LABELS: Record<EventTemplateCategory, string> = {
  inspection: 'Осмотр',
  measurement: 'Измерение',
  treatment: 'Обработка',
  care: 'Уход',
  harvest: 'Урожай',
  maintenance: 'Обслуживание',
  lifecycle: 'Жизненный цикл',
};

const CATEGORY_COLORS: Record<EventTemplateCategory, string> = {
  inspection: 'orange',
  measurement: 'blue',
  treatment: 'red',
  care: 'green',
  harvest: 'gold',
  maintenance: 'cyan',
  lifecycle: 'purple',
};

const DATA_TYPE_LABELS: Record<string, string> = {
  text: 'Текст',
  number: 'Число',
  boolean: 'Да/Нет',
  decimal: 'Дробное',
  integer: 'Число',
  float: 'Дробное',
  date: 'Дата',
  datetime: 'Дата+время',
};

const propertyColumns: ColumnsType<EventTemplateProperty> = [
  {
    title: 'Свойство',
    dataIndex: 'name',
    render: (name: string, record) => (
      <Space direction="vertical" size={0}>
        <Text strong>{name}</Text>
        {record.code && <Text type="secondary" style={{ fontSize: 12 }}>{record.code}</Text>}
      </Space>
    ),
  },
  {
    title: 'Тип данных',
    dataIndex: 'dataType',
    width: 120,
    render: (dt: string) => DATA_TYPE_LABELS[dt] ?? dt,
  },
  {
    title: 'Единица',
    dataIndex: 'unit',
    width: 100,
    render: (unit?: string) => unit || '—',
  },
  {
    title: 'Обязательное',
    dataIndex: 'isRequired',
    width: 120,
    render: (required: boolean) => required ? <Tag color="red">Да</Tag> : <Tag>Нет</Tag>,
  },
  {
    title: 'Варианты',
    dataIndex: 'options',
    render: (options?: EventTemplateProperty['options']) => {
      if (!options || options.length === 0) return <Text type="secondary">—</Text>;
      return (
        <Space size={[4, 4]} wrap>
          {options.map((opt) => (
            <Tag key={opt.id}>{opt.value}</Tag>
          ))}
        </Space>
      );
    },
  },
];

interface TemplateFormValues {
  code: string;
  name: string;
  description?: string;
  category: EventTemplateCategory;
  assignable: boolean;
  autoComplete: boolean;
  icon?: string;
  targetType?: string;
}

const AdminEventTemplatesPage: React.FC = () => {
  const [categoryFilter, setCategoryFilter] = useState<EventTemplateCategory | 'all'>('all');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'system' | 'farm'>('all');
  const [search, setSearch] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EventTemplate | null>(null);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [requiredPropertyIds, setRequiredPropertyIds] = useState<Set<number>>(new Set());
  const [form] = Form.useForm<TemplateFormValues>();

  const adminScopeId = useSelector((state: any) => state.saves?.activeSaveId || '') as string;

  const {
    data: templatesResponse,
    isFetching: loading,
    isError,
  } = useGetEventTemplatesQuery({ saveId: adminScopeId }, { skip: !adminScopeId });

  const { data: availableProperties = [] } = useGetPropertiesQuery('planting');

  const [createTemplate, { isLoading: creating }] = useCreateEventTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateEventTemplateMutation();
  const [deleteTemplate] = useDeleteEventTemplateMutation();

  const templates = useMemo(() => templatesResponse?.templates ?? [], [templatesResponse]);

  const filtered = useMemo(() => {
    let list = templates;
    if (categoryFilter !== 'all') {
      list = list.filter((t) => t.category === categoryFilter);
    }
    if (scopeFilter !== 'all') {
      list = list.filter((t) => t.scopeType === scopeFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.code.toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [templates, categoryFilter, scopeFilter, search]);

  const grouped = useMemo(() => {
    const map = new Map<EventTemplateCategory, EventTemplate[]>();
    for (const t of filtered) {
      const cat = t.category;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(t);
    }
    const order: EventTemplateCategory[] = ['lifecycle', 'inspection', 'measurement', 'care', 'treatment', 'harvest', 'maintenance'];
    return order.filter((c) => map.has(c)).map((c) => ({ category: c, templates: map.get(c)! }));
  }, [filtered]);

  // Transfer data source for property selection
  const transferDataSource = useMemo(() =>
    availableProperties.map((p: Property) => ({
      key: String(p.id),
      title: p.name,
      description: `${p.code} — ${DATA_TYPE_LABELS[p.dataType] ?? p.dataType}${p.unit ? ` (${p.unit})` : ''}`,
    })),
    [availableProperties],
  );

  const openCreateModal = useCallback(() => {
    form.resetFields();
    setSelectedPropertyIds([]);
    setRequiredPropertyIds(new Set());
    setCreateModalOpen(true);
  }, [form]);

  const openEditModal = useCallback((template: EventTemplate) => {
    setEditingTemplate(template);
    form.setFieldsValue({
      code: template.code,
      name: template.name,
      description: template.description,
      category: template.category,
      assignable: template.assignable,
      autoComplete: template.autoComplete,
      icon: template.icon,
      targetType: template.targetType ?? undefined,
    });
    const propIds = (template.properties ?? []).map((p) => String(p.propertyId));
    setSelectedPropertyIds(propIds);
    const reqIds = new Set(
      (template.properties ?? []).filter((p) => p.isRequired).map((p) => p.propertyId),
    );
    setRequiredPropertyIds(reqIds);
  }, [form]);

  const handleCreateSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (!adminScopeId) {
        message.error('Выберите сейв для создания шаблона');
        return;
      }
      const payload: CreateEventTemplatePayload & { saveId: string } = {
        ...values,
        saveId: adminScopeId,
        propertyIds: selectedPropertyIds.map(Number),
        requiredPropertyIds: Array.from(requiredPropertyIds),
      };
      await createTemplate(payload).unwrap();
      message.success('Шаблон создан');
      setCreateModalOpen(false);
      form.resetFields();
    } catch (err: any) {
      if (err?.data?.message) message.error(err.data.message);
    }
  }, [form, adminScopeId, selectedPropertyIds, requiredPropertyIds, createTemplate]);

  const handleEditSubmit = useCallback(async () => {
    if (!editingTemplate || !adminScopeId) return;
    try {
      const values = await form.validateFields();
      await updateTemplate({
        id: editingTemplate.id,
        saveId: adminScopeId,
        payload: {
          name: values.name,
          description: values.description,
          assignable: values.assignable,
          autoComplete: values.autoComplete,
          icon: values.icon,
        },
      }).unwrap();
      message.success('Шаблон обновлён');
      setEditingTemplate(null);
    } catch (err: any) {
      if (err?.data?.message) message.error(err.data.message);
    }
  }, [editingTemplate, adminScopeId, form, updateTemplate]);

  const handleDelete = useCallback((template: EventTemplate) => {
    if (!adminScopeId) return;
    Modal.confirm({
      title: `Деактивировать шаблон «${template.name}»?`,
      content: 'Шаблон будет помечен как неактивный и перестанет отображаться при создании событий.',
      okText: 'Деактивировать',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteTemplate({ id: template.id, saveId: adminScopeId }).unwrap();
          message.success('Шаблон деактивирован');
        } catch (err: any) {
          if (err?.data?.message) message.error(err.data.message);
        }
      },
    });
  }, [adminScopeId, deleteTemplate]);

  const toggleRequired = useCallback((propertyId: number, checked: boolean) => {
    setRequiredPropertyIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(propertyId);
      else next.delete(propertyId);
      return next;
    });
  }, []);

  const renderTemplateForm = () => (
    <Form form={form} layout="vertical">
      <Form.Item name="code" label="Код (латиница)" rules={[{ required: true, message: 'Введите код' }]}>
        <Input placeholder="my_custom_template" disabled={!!editingTemplate} />
      </Form.Item>
      <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
        <Input placeholder="Мой шаблон осмотра" />
      </Form.Item>
      <Form.Item name="description" label="Описание">
        <Input.TextArea rows={2} placeholder="Описание шаблона..." />
      </Form.Item>
      <Form.Item name="category" label="Категория" rules={[{ required: true }]}>
        <Select
          options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
          disabled={!!editingTemplate}
        />
      </Form.Item>
      <Form.Item name="targetType" label="Тип цели">
        <Select
          allowClear
          placeholder="Любой"
          options={[
            { value: 'planting', label: 'Посадка' },
            { value: 'bed', label: 'Грядка' },
            { value: 'any', label: 'Любой' },
          ]}
        />
      </Form.Item>
      <Space>
        <Form.Item name="assignable" valuePropName="checked">
          <Checkbox>Назначаемый</Checkbox>
        </Form.Item>
        <Form.Item name="autoComplete" valuePropName="checked">
          <Checkbox>Авто-завершение</Checkbox>
        </Form.Item>
      </Space>
      <Form.Item name="icon" label="Иконка (emoji)">
        <Input placeholder="🔍" style={{ width: 80 }} />
      </Form.Item>

      {!editingTemplate && (
        <>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Привязка свойств
          </Typography.Text>
          <Transfer
            dataSource={transferDataSource}
            targetKeys={selectedPropertyIds}
            onChange={(nextTargetKeys) => setSelectedPropertyIds(nextTargetKeys as string[])}
            render={(item) => item.title ?? ''}
            titles={['Доступные', 'Выбранные']}
            showSearch
            filterOption={(input, item) =>
              (item.title ?? '').toLowerCase().includes(input.toLowerCase()) ||
              (item.description ?? '').toLowerCase().includes(input.toLowerCase())
            }
            listStyle={{ width: 280, height: 300 }}
          />
          {selectedPropertyIds.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                Обязательные свойства:
              </Typography.Text>
              {selectedPropertyIds.map((idStr) => {
                const prop = availableProperties.find((p: Property) => String(p.id) === idStr);
                if (!prop) return null;
                return (
                  <div key={idStr} style={{ marginBottom: 2 }}>
                    <Checkbox
                      checked={requiredPropertyIds.has(prop.id)}
                      onChange={(e) => toggleRequired(prop.id, e.target.checked)}
                    >
                      {prop.name}
                    </Checkbox>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Form>
  );

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: 'Администрирование' },
            { title: 'Шаблоны событий' },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          <div css={styles.headerRow}>
            <Space direction="vertical" size={4}>
              <Title level={4} css={styles.titleNoMargin}>
                Шаблоны событий
              </Title>
              <Paragraph type="secondary" css={styles.paragraphNoMargin}>
                Шаблоны задач и мероприятий с предустановленными свойствами.
                Системные шаблоны доступны всем, пользовательские — только в конкретном сейве.
              </Paragraph>
            </Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              disabled={!adminScopeId}
              title={!adminScopeId ? 'Выберите сейв' : undefined}
            >
              Создать шаблон
            </Button>
          </div>
        </Card>

        <Card>
          <Space css={commonStyles.fullWidth} align="center" wrap>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              css={styles.selectWidth220}
              options={[
                { value: 'all', label: 'Все категории' },
                ...Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label })),
              ]}
            />
            <Select
              value={scopeFilter}
              onChange={setScopeFilter}
              css={styles.selectWidth220}
              options={[
                { value: 'all', label: 'Все типы' },
                { value: 'system', label: 'Системные' },
                { value: 'farm', label: 'Фермерские' },
              ]}
            />
            <Input.Search
              placeholder="Поиск по названию"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              css={styles.searchMaxWidth}
            />
          </Space>
        </Card>

        {loading && <Spin size="large" />}
        {isError && <Card><Empty description="Ошибка загрузки шаблонов" /></Card>}

        {!loading && !isError && grouped.length === 0 && (
          <Card><Empty description="Шаблоны не найдены" /></Card>
        )}

        {grouped.map(({ category, templates: catTemplates }) => (
          <Card key={category} title={
            <Space>
              <Tag color={CATEGORY_COLORS[category]}>{CATEGORY_LABELS[category]}</Tag>
              <Text type="secondary">{catTemplates.length} шаблон(ов)</Text>
            </Space>
          }>
            <Collapse
              items={catTemplates.map((template) => ({
                key: template.id,
                label: (
                  <div css={styles.templateHeader}>
                    {template.icon && <span css={styles.templateIcon}>{template.icon}</span>}
                    {template.color && <span css={styles.colorDot} style={{ backgroundColor: template.color }} />}
                    <Text strong>{template.name}</Text>
                    <Tag color={template.scopeType === 'system' ? 'purple' : 'green'}>
                      {template.scopeType === 'system' ? 'Системный' : 'Фермерский'}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>({template.code})</Text>
                    {!template.isActive && <Tag color="red">Неактивен</Tag>}
                    {template.scopeType === 'farm' && (
                      <span css={styles.templateActions} onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => openEditModal(template)}
                        />
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete(template)}
                        />
                      </span>
                    )}
                  </div>
                ),
                children: (
                  <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
                    {template.description && (
                      <Paragraph type="secondary" css={styles.paragraphNoMargin}>
                        {template.description}
                      </Paragraph>
                    )}
                    <Space wrap>
                      {template.assignable && <Tag color="blue">Назначаемый</Tag>}
                      {template.autoComplete && <Tag color="green">Авто-завершение</Tag>}
                      {template.targetType && <Tag>Цель: {template.targetType}</Tag>}
                    </Space>
                    {template.properties && template.properties.length > 0 ? (
                      <Table
                        css={styles.propertyTable}
                        columns={propertyColumns}
                        dataSource={template.properties}
                        rowKey="propertyId"
                        size="small"
                        pagination={false}
                      />
                    ) : (
                      <Text type="secondary">Свойства не привязаны к шаблону</Text>
                    )}
                  </Space>
                ),
              }))}
            />
          </Card>
        ))}
      </Space>

      {/* Create Template Modal */}
      <Modal
        title="Создать шаблон события"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreateSubmit}
        confirmLoading={creating}
        okText="Создать"
        cancelText="Отмена"
        width={700}
        destroyOnClose
      >
        {renderTemplateForm()}
      </Modal>

      {/* Edit Template Modal */}
      <Modal
        title={`Редактировать: ${editingTemplate?.name ?? ''}`}
        open={!!editingTemplate}
        onCancel={() => setEditingTemplate(null)}
        onOk={handleEditSubmit}
        confirmLoading={updating}
        okText="Сохранить"
        cancelText="Отмена"
        width={700}
        destroyOnClose
      >
        {renderTemplateForm()}
      </Modal>
    </MainLayout>
  );
};

export default AdminEventTemplatesPage;
